// Standard, globally-available project fields.
//
// Categories, types, and subtypes are now managed via the project_taxonomy table
// (Admin > Platform Settings > Project Taxonomy). The hardcoded arrays below
// serve as the fallback when the taxonomy API hasn't been loaded yet or returns
// an empty result.
//
// Call `hydrateTaxonomyFromApi()` at app startup to overlay DB-managed taxonomy.

import i18n from "../../i18n/config";
import type { TaxonomyNode, TaxonomyTranslations } from "../../api/projectTaxonomy";

// Cast to bypass i18next v25's strict 0-arg type signature (runtime accepts a key string).
 
const t = i18n.t.bind(i18n) as (key: string) => string;

export const projectStatusOptions = [
  "Proposed",
  "Planned",
  "In Progress",
  "Abandoned",
  "Completed",
] as const;

// Content lifecycle/status for narrative + evidence/verification.
export const contentStatusOptions = [
  "Drafting",
  "Under Review",
  "Peer Reviewed",
  "Published",
  "Archived",
] as const;

/* ── i18n helpers ─────────────────────────────────────────────
 * Convert a display-label value (e.g. "In Progress") to its
 * locale key (e.g. "in_progress") and look it up in the
 * projectConfig namespace.  Falls back to the original string. */

const toKey = (s: string) =>
  s.toLowerCase().replace(/&/g, "").replace(/\s+/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, "");

export function localizedProjectStatus(status: string): string {
  const key = `projectConfig:enums.projectStatus.${toKey(status)}`;
  const result = t(key);
  return result === key ? status : result;
}

export function localizedContentStatus(status: string): string {
  const key = `projectConfig:enums.contentStatus.${toKey(status)}`;
  const result = t(key);
  return result === key ? status : result;
}

/**
 * Resolve a DB-sourced translation for the current i18n language.
 * Returns undefined if no DB translation exists for this label+locale.
 */
function dbTranslation(label: string): string | undefined {
  const lang = i18n.language?.split("-")[0]; // "en-US" → "en"
  if (!lang || lang === "en") return undefined; // English IS the label column
  const entry = __dbTranslations[label.toLowerCase()];
  return entry?.[lang] || undefined;
}

export function localizedProjectCategory(category: string): string {
  // 1) DB translation (admin-managed)
  const db = dbTranslation(category);
  if (db) return db;
  // 2) Static locale JSON
  const key = `projectConfig:enums.projectCategory.${toKey(category)}`;
  const result = t(key);
  // 3) Fallback to English label
  return result === key ? category : result;
}

/** Localised type label. */
export function localizedProjectType(typeValue: string): string {
  const db = dbTranslation(typeValue);
  if (db) return db;
  const key = `projectConfig:enums.projectType.${toKey(typeValue)}`;
  const result = t(key);
  return result === key ? typeValue : result;
}

/** Localised subtype label. */
export function localizedProjectSubtype(subtypeValue: string): string {
  const db = dbTranslation(subtypeValue);
  if (db) return db;
  const key = `projectConfig:enums.projectSubtype.${toKey(subtypeValue)}`;
  const result = t(key);
  return result === key ? subtypeValue : result;
}

/* ── Project Types & Subtypes ──────────────────────────────────
 * These interfaces and the hardcoded map are the FALLBACK source.
 * Once `hydrateTaxonomyFromApi()` is called with API data, the
 * runtime getters (`getActiveCategoryOptions`, `getTypesForCategory`,
 * `getSubtypesForType`) use the API-sourced data instead.
 * ─────────────────────────────────────────────────────────────── */

export interface ProjectSubtype {
  value: string;
}

export interface ProjectType {
  value: string;
  subtypes: ProjectSubtype[];
}

// Hardcoded fallback — used when taxonomy API isn't available or empty.
const FALLBACK_CATEGORIES: readonly string[] = [
  "Infrastructure",
  "Education",
  "Environment",
  "Public Health",
  "Housing",
  "Economic Development",
  "Public Safety",
  "Governance",
  "Arts & Culture",
  "Other",
];

const FALLBACK_TYPES_BY_CATEGORY: Record<string, ProjectType[]> = {
  Infrastructure: [
    { value: 'Roads & Bridges', subtypes: [{ value: 'New Construction' }, { value: 'Repair & Maintenance' }, { value: 'Expansion' }] },
    { value: 'Water & Sanitation', subtypes: [{ value: 'Water Supply' }, { value: 'Wastewater Treatment' }, { value: 'Stormwater Management' }] },
    { value: 'Energy', subtypes: [{ value: 'Solar' }, { value: 'Wind' }, { value: 'Grid Modernization' }, { value: 'Battery Storage' }] },
    { value: 'Telecommunications', subtypes: [{ value: 'Broadband' }, { value: 'Cell Towers' }, { value: 'Fiber Optic' }] },
    { value: 'Public Facilities', subtypes: [{ value: 'Community Centers' }, { value: 'Libraries' }, { value: 'Parks & Recreation' }] },
  ],
  Education: [
    { value: 'School Construction', subtypes: [{ value: 'Primary School' }, { value: 'Secondary School' }, { value: 'University' }] },
    { value: 'Curriculum Development', subtypes: [{ value: 'STEM' }, { value: 'Literacy' }, { value: 'Vocational Training' }] },
    { value: 'Teacher Training', subtypes: [] },
    { value: 'Digital Learning', subtypes: [{ value: 'E-Learning Platform' }, { value: 'Device Distribution' }, { value: 'Digital Literacy' }] },
    { value: 'Scholarship & Access', subtypes: [] },
  ],
  Environment: [
    { value: 'Conservation', subtypes: [{ value: 'Wildlife Habitat' }, { value: 'Forest Preservation' }, { value: 'Marine Protection' }] },
    { value: 'Restoration', subtypes: [{ value: 'Reforestation' }, { value: 'Wetland Restoration' }, { value: 'River Cleanup' }] },
    { value: 'Climate Adaptation', subtypes: [{ value: 'Flood Defense' }, { value: 'Drought Resilience' }, { value: 'Heat Mitigation' }] },
    { value: 'Pollution Reduction', subtypes: [{ value: 'Air Quality' }, { value: 'Waste Management' }, { value: 'Recycling' }] },
    { value: 'Sustainability', subtypes: [{ value: 'Green Building' }, { value: 'Circular Economy' }, { value: 'Carbon Offset' }] },
  ],
  'Public Health': [
    { value: 'Healthcare Facilities', subtypes: [{ value: 'Hospital' }, { value: 'Clinic' }, { value: 'Mobile Health Unit' }] },
    { value: 'Disease Prevention', subtypes: [{ value: 'Vaccination' }, { value: 'Screening' }, { value: 'Vector Control' }] },
    { value: 'Mental Health', subtypes: [{ value: 'Counseling Services' }, { value: 'Crisis Intervention' }, { value: 'Awareness Campaign' }] },
    { value: 'Nutrition & Food Security', subtypes: [{ value: 'Food Banks' }, { value: 'Community Gardens' }, { value: 'School Meals' }] },
    { value: 'Emergency Preparedness', subtypes: [] },
  ],
  Housing: [
    { value: 'Affordable Housing', subtypes: [{ value: 'New Build' }, { value: 'Conversion' }, { value: 'Land Trust' }] },
    { value: 'Homeless Services', subtypes: [{ value: 'Shelter' }, { value: 'Transitional Housing' }, { value: 'Supportive Housing' }] },
    { value: 'Home Repair', subtypes: [{ value: 'Weatherization' }, { value: 'Accessibility Retrofit' }, { value: 'Safety Upgrades' }] },
    { value: 'Senior Housing', subtypes: [] },
  ],
  'Economic Development': [
    { value: 'Small Business Support', subtypes: [{ value: 'Microloans' }, { value: 'Incubator' }, { value: 'Mentorship' }] },
    { value: 'Workforce Development', subtypes: [{ value: 'Job Training' }, { value: 'Apprenticeship' }, { value: 'Career Counseling' }] },
    { value: 'Tourism', subtypes: [{ value: 'Eco-Tourism' }, { value: 'Heritage Tourism' }, { value: 'Agri-Tourism' }] },
    { value: 'Market Development', subtypes: [{ value: 'Farmers Market' }, { value: 'Trade Facilitation' }, { value: 'Export Assistance' }] },
  ],
  'Public Safety': [
    { value: 'Community Policing', subtypes: [] },
    { value: 'Fire Safety', subtypes: [{ value: 'Equipment' }, { value: 'Training' }, { value: 'Prevention' }] },
    { value: 'Disaster Response', subtypes: [{ value: 'Early Warning' }, { value: 'Evacuation Planning' }, { value: 'Recovery' }] },
    { value: 'Violence Prevention', subtypes: [{ value: 'Youth Programs' }, { value: 'Conflict Resolution' }, { value: 'Domestic Violence Support' }] },
  ],
  Governance: [
    { value: 'Civic Engagement', subtypes: [{ value: 'Voter Registration' }, { value: 'Public Forums' }, { value: 'Participatory Budgeting' }] },
    { value: 'Transparency', subtypes: [{ value: 'Open Data' }, { value: 'Audit' }, { value: 'Anti-Corruption' }] },
    { value: 'Policy Development', subtypes: [] },
    { value: 'Digital Government', subtypes: [{ value: 'E-Services' }, { value: 'Data Analytics' }, { value: 'Citizen Portal' }] },
  ],
  'Arts & Culture': [
    { value: 'Public Art', subtypes: [{ value: 'Murals' }, { value: 'Sculpture' }, { value: 'Performance' }] },
    { value: 'Cultural Heritage', subtypes: [{ value: 'Preservation' }, { value: 'Museums' }, { value: 'Oral History' }] },
    { value: 'Community Events', subtypes: [{ value: 'Festivals' }, { value: 'Workshops' }, { value: 'Exhibitions' }] },
    { value: 'Media & Storytelling', subtypes: [{ value: 'Documentary' }, { value: 'Community Radio' }, { value: 'Digital Archive' }] },
  ],
  Other: [
    { value: 'Research', subtypes: [] },
    { value: 'Pilot Program', subtypes: [] },
    { value: 'Multi-Sector', subtypes: [] },
  ],
};

/* ── Runtime taxonomy state ──────────────────────────────────
 * These module-level variables hold the API-sourced taxonomy
 * after `hydrateTaxonomyFromApi()` is called. All public getters
 * consult these first, falling back to the hardcoded arrays. */

let __taxonomyHydrated = false;
let __apiCategories: string[] = [];
let __apiTypesByCategory: Record<string, ProjectType[]> = {};
/**
 * DB-sourced translations keyed by English label (lowercased).
 * Populated by hydrateTaxonomyFromApi().
 * Shape: { "public health": { ar: "...", fr: "..." }, ... }
 */
let __dbTranslations: Record<string, TaxonomyTranslations> = {};

/**
 * Call once at app startup after fetching from GET /api/ProjectTaxonomy.
 * Converts the flat node array into the category/type/subtype structure
 * that the rest of the app expects, respecting is_active flags.
 */
export function hydrateTaxonomyFromApi(nodes: TaxonomyNode[]): void {
  if (!nodes || nodes.length === 0) return;

  // Build guid → node lookup
  const byGuid: Record<string, TaxonomyNode> = {};
  for (const n of nodes) byGuid[n.taxonomyGuid] = n;

  // Build translations lookup (keyed by lowercased English label)
  const txMap: Record<string, TaxonomyTranslations> = {};
  for (const n of nodes) {
    if (n.translations && Object.keys(n.translations).length > 0) {
      txMap[n.label.toLowerCase()] = n.translations;
    }
  }

  // Extract active categories (sorted)
  const cats = nodes
    .filter((n) => n.level === 'category' && n.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label));

  const catLabels: string[] = [];
  const typeMap: Record<string, ProjectType[]> = {};

  for (const cat of cats) {
    catLabels.push(cat.label);

    // Types under this category
    const types = nodes
      .filter((n) => n.level === 'type' && n.parentTaxonomyGuid === cat.taxonomyGuid && n.isActive)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label));

    const typeDefs: ProjectType[] = [];
    for (const typ of types) {
      // Subtypes under this type
      const subs = nodes
        .filter((n) => n.level === 'subtype' && n.parentTaxonomyGuid === typ.taxonomyGuid && n.isActive)
        .sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label))
        .map((s) => ({ value: s.label }));

      typeDefs.push({ value: typ.label, subtypes: subs });
    }

    typeMap[cat.label] = typeDefs;
  }

  __apiCategories = catLabels;
  __apiTypesByCategory = typeMap;
  __dbTranslations = txMap;
  __taxonomyHydrated = true;
}

/** Returns true if API taxonomy data has been loaded. */
export function isTaxonomyHydrated(): boolean {
  return __taxonomyHydrated;
}

/* ── Public getters (API-first, fallback to hardcoded) ────── */

/**
 * Active project category labels.
 * Use this instead of the old `projectCategoryOptions` const where possible.
 */
export function getActiveCategoryOptions(): readonly string[] {
  return __taxonomyHydrated && __apiCategories.length > 0
    ? __apiCategories
    : FALLBACK_CATEGORIES;
}

/**
 * Backward-compatible export: consumers that read `projectCategoryOptions`
 * directly still work. Once all consumers migrate to `getActiveCategoryOptions()`,
 * this can be removed.
 */
export const projectCategoryOptions = FALLBACK_CATEGORIES;

/**
 * Backward-compatible export: the full hardcoded map.
 * Prefer `getTypesForCategory()` and `getSubtypesForType()` instead.
 */
export const projectTypesByCategory = FALLBACK_TYPES_BY_CATEGORY;

/** Get the flat list of active type values for a given category. */
export function getTypesForCategory(category: string): string[] {
  const source = __taxonomyHydrated ? __apiTypesByCategory : FALLBACK_TYPES_BY_CATEGORY;
  return (source[category] || []).map((t) => t.value);
}

/** Get the flat list of active subtype values for a given category + type. */
export function getSubtypesForType(category: string, typeValue: string): string[] {
  const source = __taxonomyHydrated ? __apiTypesByCategory : FALLBACK_TYPES_BY_CATEGORY;
  const types = source[category] || [];
  const found = types.find((t) => t.value === typeValue);
  return found ? found.subtypes.map((s) => s.value) : [];
}
